import api from '@/lib/api';
import type {
  CreateEmployeeProfileBlockInput,
  EmployeeProfileBlock,
  EmployeeProfileSection,
  RenameEmployeeProfileBlockInput,
  ReorderEmployeeProfileBlocksInput,
} from '@hygge/shared';
import { CACHE_TIME } from '@hygge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const employeeProfileBlocksKeys = {
  all: ['employee-profile-blocks'] as const,
  bySection: (section: EmployeeProfileSection) =>
    [...employeeProfileBlocksKeys.all, { section }] as const,
};

async function listBlocks(section: EmployeeProfileSection): Promise<EmployeeProfileBlock[]> {
  const { data } = await api.get<EmployeeProfileBlock[]>('/employee-profile-blocks', {
    params: { section },
  });
  return data;
}

async function createBlock(
  input: CreateEmployeeProfileBlockInput,
): Promise<EmployeeProfileBlock> {
  const { data } = await api.post<EmployeeProfileBlock>('/employee-profile-blocks', input);
  return data;
}

async function renameBlock(
  id: string,
  input: RenameEmployeeProfileBlockInput,
): Promise<EmployeeProfileBlock> {
  const { data } = await api.patch<EmployeeProfileBlock>(
    `/employee-profile-blocks/${id}`,
    input,
  );
  return data;
}

async function deleteBlock(id: string): Promise<void> {
  await api.delete(`/employee-profile-blocks/${id}`);
}

async function reorderBlocks(
  input: ReorderEmployeeProfileBlocksInput,
): Promise<EmployeeProfileBlock[]> {
  const { data } = await api.post<EmployeeProfileBlock[]>(
    '/employee-profile-blocks/reorder',
    input,
  );
  return data;
}

export function useEmployeeProfileBlocks(section: EmployeeProfileSection) {
  return useQuery({
    queryKey: employeeProfileBlocksKeys.bySection(section),
    queryFn: () => listBlocks(section),
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useCreateEmployeeProfileBlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createBlock,
    meta: { suppressGlobalToast: true },
    onSuccess: (block) => {
      queryClient.invalidateQueries({
        queryKey: employeeProfileBlocksKeys.bySection(block.section),
      });
    },
  });
}

export function useRenameEmployeeProfileBlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      renameBlock(id, { name }),
    meta: { suppressGlobalToast: true },
    onSuccess: (block) => {
      queryClient.invalidateQueries({
        queryKey: employeeProfileBlocksKeys.bySection(block.section),
      });
    },
  });
}

export function useDeleteEmployeeProfileBlock(section: EmployeeProfileSection) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteBlock,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeeProfileBlocksKeys.bySection(section),
      });
    },
  });
}

export function useReorderEmployeeProfileBlocks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reorderBlocks,
    // Rollback-only onError; call sites supply their own toast. Explicit flag for consistency.
    meta: { suppressGlobalToast: true },
    onMutate: async (input) => {
      await queryClient.cancelQueries({
        queryKey: employeeProfileBlocksKeys.bySection(input.section),
      });
      const previous = queryClient.getQueryData<EmployeeProfileBlock[]>(
        employeeProfileBlocksKeys.bySection(input.section),
      );
      if (previous) {
        const lookup = new Map(previous.map((b) => [b.id, b]));
        const optimistic = input.blockIds
          .map((id, index) => {
            const block = lookup.get(id);
            return block ? { ...block, position: index } : null;
          })
          .filter((b): b is EmployeeProfileBlock => b !== null);
        queryClient.setQueryData(
          employeeProfileBlocksKeys.bySection(input.section),
          optimistic,
        );
      }
      return { previous };
    },
    onError: (_err, input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          employeeProfileBlocksKeys.bySection(input.section),
          context.previous,
        );
      }
    },
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({
        queryKey: employeeProfileBlocksKeys.bySection(input.section),
      });
    },
  });
}
